import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material';
import { StageConstants } from '../../core/constants';
import { ProductService } from '../../core/product/product.service';
import { EventTrackingService
       } from '../../core/tracking/event-tracking.service';
import { StageUtils } from '../../core/utils.stage';
import { AbstractButtonConfig, ButtonsAssignment
       } from '../dialog-buttons.component';

import { DialogComponent } from '../dialog.component';

export enum UnchangedPlaceholdersChoice
{
  FIX,
  IGNORE,
  CLEAR,
  IGNORE_IMAGES_CLEAR_TITLE,
}

export enum PlaceholderKind
{
  IMAGE,
  TEXT,
}

export interface PlaceholderContext
{
  id: string;
  kind: PlaceholderKind;
  textContent: string | undefined;
  node: Element;
}

export interface UnchangedPlaceholdersContext
{
  componentType: string | undefined;
  placeholders: Array<PlaceholderContext>;
  changeableElementsCount: number;
}

export function isVolatileText(
  context: UnchangedPlaceholdersContext, placeholder: PlaceholderContext
): boolean
{
  return (isBoxTitle(context, placeholder) ||
          isUserDefined(context, placeholder));
}

function isUserDefined(
  context: UnchangedPlaceholdersContext, placeholder: PlaceholderContext
): boolean
{
  return StageUtils.getBooleanAttribute(
      placeholder.node, StageConstants.ATTRIBUTE_IS_VOLATILE_IF_UNCHANGED);
}

function isBoxTitle(
  context: UnchangedPlaceholdersContext, placeholder: PlaceholderContext
): boolean
{
  return (StageUtils.isBoxComponentType(context.componentType || '') &&
          placeholder.id === StageConstants.BOX_TITLE_TEXT_ID);
}

type UnchangedPlaceholdersButtonConfig =
    AbstractButtonConfig<UnchangedPlaceholdersChoice>;

enum DialogVersion {
  TEXT,
  IMAGE,
  MISC,
  BOX_TITLE
}

interface UIValues {
  titleKey: string;
  messageKey: string;
  iconUrl: string;
  buttons: ButtonsAssignment;
}

class UnchangedPlaceholdersButton
{
  public static FIX: UnchangedPlaceholdersButtonConfig = {
    labelKey: 'i18n.button.cancelAndFix',
    choice: UnchangedPlaceholdersChoice.FIX,
  };

  public static IGNORE: UnchangedPlaceholdersButtonConfig = {
    labelKey: 'i18n.button.ignoreAndContinue',
    choice: UnchangedPlaceholdersChoice.IGNORE,
  };

  public static CLEAR: UnchangedPlaceholdersButtonConfig = {
    labelKey: 'i18n.dialog.unchangedPlaceholders.buttons.clear.label',
    choice: UnchangedPlaceholdersChoice.CLEAR,
  };

  public static IGNORE_IMAGES_CLEAR_TITLE: UnchangedPlaceholdersButtonConfig = {
    labelKey: 'i18n.button.ignoreAndContinue',
    choice: UnchangedPlaceholdersChoice.IGNORE_IMAGES_CLEAR_TITLE,
  };
}

@Component({
  selector: 'unchanged-placeholders-dialog',
  templateUrl: './unchanged-placeholders-dialog.component.html',
  styleUrls: ['../dialog.scss']
})
export class UnchangedPlaceholdersDialogComponent extends DialogComponent
{
  public unchangedPlaceholdersCount: number;
  public uiValues: UIValues;
  public textContent: string | undefined;

  constructor(@Inject(MAT_DIALOG_DATA) protected dialogData: any,
              @Inject(EventTrackingService) private eventTrackingService,
              @Inject(ProductService) private productService)
  {
    super(dialogData);

    this.uiValues = this.determineUIValues(dialogData);
    this.unchangedPlaceholdersCount = this.dialogData.placeholders.length;

    this.textContent = this.dialogData.placeholders[0].textContent;
  }

  private determineUIValues(context: UnchangedPlaceholdersContext): UIValues
  {
    switch (this.determineDialogVersion(context)) {
      case DialogVersion.TEXT: return {
        titleKey: 'i18n.dialog.unchangedPlaceholders.text.title',
        messageKey: 'i18n.dialog.unchangedPlaceholders.text.message',
        iconUrl: '/assets/img/msg-unchanged-text.svg',
        buttons: {
          primary: UnchangedPlaceholdersButton.FIX,
          secondary: UnchangedPlaceholdersButton.IGNORE,
        },
      };
      case DialogVersion.IMAGE: return {
        titleKey: 'i18n.dialog.unchangedPlaceholders.image.title',
        messageKey: 'i18n.dialog.unchangedPlaceholders.image.message',
        iconUrl: '/assets/img/msg-unchanged-image.svg',
        buttons: {
          primary: UnchangedPlaceholdersButton.FIX,
          secondary: (
            (context.changeableElementsCount - context.placeholders.length > 0)
                ? UnchangedPlaceholdersButton.IGNORE
                : undefined),
        },
      };
      case DialogVersion.MISC: return {
        titleKey: 'i18n.dialog.unchangedPlaceholders.misc.title',
        messageKey: 'i18n.dialog.unchangedPlaceholders.misc.message',
        iconUrl: '/assets/img/msg-unchanged-misc.svg',
        buttons: {
          primary: UnchangedPlaceholdersButton.FIX,
          secondary: UnchangedPlaceholdersButton.IGNORE_IMAGES_CLEAR_TITLE,
        },
      };
      case DialogVersion.BOX_TITLE: return {
        titleKey: 'i18n.dialog.unchangedPlaceholders.boxTitle.title',
        messageKey: 'i18n.dialog.unchangedPlaceholders.boxTitle.message',
        iconUrl: '/assets/img/msg-unchanged-boxTitle.svg',
        buttons: {
          primary: UnchangedPlaceholdersButton.FIX,
          secondary: UnchangedPlaceholdersButton.CLEAR,
        },
      };
      default: return <any>undefined; // cannot be reached
    }
  }

  private determineDialogVersion(
    ctx: UnchangedPlaceholdersContext): DialogVersion
  {
    const isOneKind = (kind: PlaceholderKind) =>
      ctx.placeholders.every(p => p.kind === kind);

    if (isOneKind(PlaceholderKind.IMAGE)) {
      return DialogVersion.IMAGE;
    }
    else if (isOneKind(PlaceholderKind.TEXT)) {
      if (ctx.placeholders.length === 1 && isBoxTitle(ctx, ctx.placeholders[0]))
      {
        this.eventTrackingService.track({
          category: this.productService.getCurrentProduct().id,
          action: 'display_error_message_text',
          label: undefined,
          value: undefined,
        });
        return DialogVersion.BOX_TITLE;
      } else {
        return DialogVersion.TEXT;
      }
    }
    else {
      return DialogVersion.MISC;
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/dialogs/unchanged-placeholders-dialog/unchanged-placeholders-dialog.component.ts